import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import {
  PageHeader,
  PageHeaderBreadcrumb,
  PageHeaderTitle,
  PageHeaderMeta,
  PageHeaderRow,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/lib/api/client";
import type { ReportGroupBy, ReportsPayload } from "@/lib/api/types";
import { formatCurrency } from "@/lib/tms/format";
import { BarChart3, Download, FileSpreadsheet, TrendingUp } from "lucide-react";
import * as React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";

const GROUP_BY_OPTIONS: { value: ReportGroupBy; label: string }[] = [
  { value: "carrier", label: "Carrier" },
  { value: "lane", label: "Lane" },
  { value: "mode", label: "Mode (LTL/Parcel)" },
  { value: "status", label: "Status" },
  { value: "month", label: "Monthly Trend" },
];

const DATE_PRESETS: { label: string; from: () => string; to: () => string }[] = [
  {
    label: "This Month",
    from: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
    },
    to: () => new Date().toISOString().slice(0, 10),
  },
  {
    label: "Last Month",
    from: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10);
    },
    to: () => {
      const d = new Date();
      return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
    },
  },
  {
    label: "Last Quarter",
    from: () => {
      const d = new Date();
      const qStart = Math.floor(d.getMonth() / 3) * 3 - 3;
      return new Date(d.getFullYear(), qStart, 1).toISOString().slice(0, 10);
    },
    to: () => {
      const d = new Date();
      const qStart = Math.floor(d.getMonth() / 3) * 3;
      return new Date(d.getFullYear(), qStart, 0).toISOString().slice(0, 10);
    },
  },
  {
    label: "Year to Date",
    from: () => new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10),
    to: () => new Date().toISOString().slice(0, 10),
  },
];

const CHART_COLORS = [
  "#1A1A1A",
  "#4A4A4A",
  "#6A6A6A",
  "#8A8A8A",
  "#AAAAAA",
  "#BABABA",
  "#CACACA",
  "#DADADA",
  "#E5E5E5",
  "#F0F0F0",
];

function isValidDateRange(from: string, to: string): boolean {
  if (!from || !to) return false;
  return from <= to;
}

function getOnTimeStatus(percent: number): { label: string; className: string } {
  if (percent >= 80) {
    return { label: "Good", className: "text-emerald-600" };
  }
  if (percent >= 50) {
    return { label: "Fair", className: "text-yellow-600" };
  }
  return { label: "Poor", className: "text-red-600" };
}

function MetricSummaryCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
}) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">{label}</p>
          <p className="text-2xl font-semibold tabular-nums">{value}</p>
        </div>
        <span className="grid size-8 shrink-0 place-items-center rounded-lg border-[0.5px] border-border bg-secondary text-muted-foreground">
          <Icon className="size-4" />
        </span>
      </CardContent>
    </Card>
  );
}

function SpendBarChart({ data, groupBy }: { data: ReportsPayload; groupBy: ReportGroupBy }) {
  const chartData = data.rows.slice(0, 15).map((r) => ({
    name: r.dimension.length > 20 ? `${r.dimension.slice(0, 18)}...` : r.dimension,
    cost: r.totalCost,
    count: r.shipmentCount,
  }));

  if (chartData.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No data for selected filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 60 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="name" angle={-45} textAnchor="end" className="text-xs" interval={0} height={80} />
        <YAxis tickFormatter={(v: number) => `$${v.toLocaleString()}`} className="text-xs" />
        <Tooltip
          formatter={(value) => [formatCurrency(Number(value ?? 0)), "Total Spend"]}
          contentStyle={{
            borderRadius: "8px",
            border: "1px solid hsl(var(--border))",
            backgroundColor: "hsl(var(--card))",
            color: "hsl(var(--card-foreground))",
          }}
        />
        <Bar dataKey="cost" fill="#1A1A1A" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function SpendPieChart({ data }: { data: ReportsPayload }) {
  const chartData = data.rows.slice(0, 8).map((r) => ({
    name: r.dimension,
    value: r.totalCost,
  }));

  if (chartData.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No data for selected filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          labelLine={false}
          label={({ name, percent }: { name?: string; percent?: number }) => {
            const n = name ?? "";
            const p = percent ?? 0;
            return `${n.length > 12 ? `${n.slice(0, 10)}..` : n} ${(p * 100).toFixed(0)}%`;
          }}
          outerRadius={120}
          dataKey="value"
        >
          {chartData.map((_, idx) => (
            <Cell key={idx} fill={CHART_COLORS[idx % CHART_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value) => formatCurrency(Number(value ?? 0))} />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}

function MonthlyTrendChart({ data }: { data: ReportsPayload }) {
  const chartData = [...data.rows]
    .sort((a, b) => a.dimension.localeCompare(b.dimension))
    .map((r) => ({
      name: r.dimension,
      cost: r.totalCost,
      count: r.shipmentCount,
      avg: r.avgCostPerShipment,
    }));

  if (chartData.length === 0) {
    return <p className="py-8 text-center text-sm text-muted-foreground">No data for selected filters.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={350}>
      <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
        <XAxis dataKey="name" className="text-xs" />
        <YAxis yAxisId="cost" tickFormatter={(v: number) => `$${v.toLocaleString()}`} className="text-xs" />
        <YAxis yAxisId="count" orientation="right" className="text-xs" />
        <Tooltip
          formatter={(value, name) => [
            name === "cost" ? formatCurrency(Number(value ?? 0)) : Number(value ?? 0),
            name === "cost" ? "Total Spend" : "Shipments",
          ]}
          contentStyle={{
            borderRadius: "8px",
            border: "1px solid hsl(var(--border))",
            backgroundColor: "hsl(var(--card))",
            color: "hsl(var(--card-foreground))",
          }}
        />
        <Legend />
        <Line yAxisId="cost" type="monotone" dataKey="cost" stroke="#1A1A1A" strokeWidth={2} name="Total Spend" />
        <Line yAxisId="count" type="monotone" dataKey="count" stroke="#8A8A8A" strokeWidth={2} name="Shipments" />
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function ReportsPage() {
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [report, setReport] = React.useState<ReportsPayload | null>(null);
  const [exporting, setExporting] = React.useState(false);

  const defaultPreset = DATE_PRESETS[0]!;
  const [from, setFrom] = React.useState(defaultPreset.from());
  const [to, setTo] = React.useState(defaultPreset.to());
  const [groupBy, setGroupBy] = React.useState<ReportGroupBy>("carrier");
  const [activePreset, setActivePreset] = React.useState(0);
  const invalidDateRange = !isValidDateRange(from, to);

  const load = React.useCallback(async () => {
    if (!isValidDateRange(from, to)) {
      setLoading(false);
      setReport(null);
      setError("Start date must be before or equal to end date.");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await apiClient.getReports({ from, to, groupBy });
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to load report.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy]);

  React.useEffect(() => {
    void load();
  }, [load]);

  const handleExportCsv = React.useCallback(async () => {
    if (!isValidDateRange(from, to)) {
      setError("Start date must be before or equal to end date.");
      return;
    }

    setExporting(true);
    try {
      const blob = await apiClient.exportReportsCsv({ from, to, groupBy });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `report-${groupBy}-${from}-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed.");
    } finally {
      setExporting(false);
    }
  }, [from, to, groupBy]);

  const applyPreset = React.useCallback((idx: number) => {
    const preset = DATE_PRESETS[idx]!;
    setFrom(preset.from());
    setTo(preset.to());
    setActivePreset(idx);
  }, []);

  return (
    <div className="space-y-8">
      {/* Header */}
      <PageHeader>
        <PageHeaderBreadcrumb items={[{ label: "Analytics" }, { label: "Reports" }]} />
        <PageHeaderRow>
          <div>
            <PageHeaderTitle>Reports</PageHeaderTitle>
            <PageHeaderMeta>
              <span>Carrier spend breakdown, trends, and lane analysis.</span>
            </PageHeaderMeta>
          </div>
          <PageHeaderActions>
            <Button
              variant="outline"
              disabled={exporting || loading}
              onClick={handleExportCsv}
            >
              <Download className="size-4" />
              {exporting ? "Exporting..." : "Export CSV"}
            </Button>
          </PageHeaderActions>
        </PageHeaderRow>
      </PageHeader>

      {/* Filters */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-end gap-4">
            {/* Date presets */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Date Range</label>
              <div className="flex flex-wrap gap-1.5">
                {DATE_PRESETS.map((preset, idx) => (
                  <button
                    key={preset.label}
                    type="button"
                    onClick={() => applyPreset(idx)}
                    className={`inline-flex h-8 items-center rounded-lg border-[0.5px] px-2.5 text-xs font-medium transition-colors ${
                      activePreset === idx
                        ? "border-foreground bg-foreground text-background"
                        : "border-border bg-card text-muted-foreground hover:bg-accent"
                    }`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Custom date inputs */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">From</label>
              <DateTimePicker
                granularity="date"
                value={from}
                size="sm"
                className="min-w-[10.5rem] justify-between bg-card"
                onChange={(value) => {
                  setFrom(value);
                  setActivePreset(-1);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">To</label>
              <DateTimePicker
                granularity="date"
                value={to}
                size="sm"
                className="min-w-[10.5rem] justify-between bg-card"
                onChange={(value) => {
                  setTo(value);
                  setActivePreset(-1);
                }}
              />
            </div>

            {/* Group by */}
            <div className="space-y-1.5">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">Group By</label>
              <select
                value={groupBy}
                onChange={(e) => setGroupBy(e.target.value as ReportGroupBy)}
                className="h-8 rounded-lg border-[0.5px] border-border bg-card px-2 text-sm"
              >
                {GROUP_BY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {invalidDateRange ? (
            <p role="alert" aria-live="polite" className="mt-3 text-xs text-destructive">
              Start date must be before or equal to end date.
            </p>
          ) : null}
        </CardContent>
      </Card>

      {error ? <p className="text-sm text-destructive">{error}</p> : null}

      {/* Loading skeleton */}
      {loading ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="size-7 rounded-md" />
                  </div>
                  <Skeleton className="h-8 w-20 mt-1" />
                </CardHeader>
              </Card>
            ))}
          </div>
          <Card>
            <CardHeader>
              <Skeleton className="h-5 w-40" />
            </CardHeader>
            <CardContent>
              <Skeleton className="h-[350px] w-full rounded" />
            </CardContent>
          </Card>
        </div>
      ) : null}

      {/* Report content */}
      {report && !loading ? (
        <>
          {/* Summary metrics */}
          <section className="grid gap-4 md:grid-cols-3">
            <MetricSummaryCard
              label="Total Spend"
              value={formatCurrency(report.totals.totalCost)}
              icon={FileSpreadsheet}
            />
            <MetricSummaryCard
              label="Total Shipments"
              value={report.totals.shipmentCount.toLocaleString()}
              icon={BarChart3}
            />
            <MetricSummaryCard
              label="Avg. Cost / Shipment"
              value={formatCurrency(report.totals.avgCostPerShipment)}
              icon={TrendingUp}
            />
          </section>

          {/* Charts */}
          <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>
                  {groupBy === "month" ? "Monthly Spend Trend" : `Spend by ${GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label ?? groupBy}`}
                </CardTitle>
                <CardDescription>
                  {report.dateRange.from} to {report.dateRange.to}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {groupBy === "month" ? (
                  <MonthlyTrendChart data={report} />
                ) : (
                  <SpendBarChart data={report} groupBy={groupBy} />
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Spend Distribution</CardTitle>
                <CardDescription>
                  Top {Math.min(8, report.rows.length)} by total cost
                </CardDescription>
              </CardHeader>
              <CardContent>
                <SpendPieChart data={report} />
              </CardContent>
            </Card>
          </div>

          {/* Data table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Breakdown</CardTitle>
              <CardDescription>
                {report.rows.length} {report.rows.length === 1 ? "group" : "groups"} found
              </CardDescription>
            </CardHeader>
            <CardContent>
              {report.rows.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No data for selected filters.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left">
                        <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono">
                          {GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label ?? "Dimension"}
                        </th>
                        <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono text-right">Total Spend</th>
                        <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono text-right">Shipments</th>
                        <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono text-right">Avg Cost</th>
                        <th className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-mono text-right">Delivered %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.rows.map((row) => (
                        <tr key={row.dimension} className="border-b last:border-0 hover:bg-muted/50">
                          <td className="px-3 py-2 font-medium">{row.dimension}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.totalCost)}</td>
                          <td className="px-3 py-2 text-right">{row.shipmentCount.toLocaleString()}</td>
                          <td className="px-3 py-2 text-right">{formatCurrency(row.avgCostPerShipment)}</td>
                          <td className="px-3 py-2 text-right">
                            {(() => {
                              const status = getOnTimeStatus(row.onTimePercent);
                              return (
                                <span aria-label={`On-time ${row.onTimePercent}% (${status.label})`} className={status.className}>
                                  {row.onTimePercent}% ({status.label})
                                </span>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t font-semibold">
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(report.totals.totalCost)}</td>
                        <td className="px-3 py-2 text-right">{report.totals.shipmentCount.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(report.totals.avgCostPerShipment)}</td>
                        <td className="px-3 py-2 text-right">-</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}
